///---------------------------------------------------------------------------
//! Copyright (C) ASQUARED SRL - All Rights Reserved
//* Unauthorized copying of this file, via any medium is strictly prohibited
//* Proprietary and confidential
//* Written by Alexandru Gârbacea <g99.alex@yahoo.com>, September 2022
//? @author g99.alex@yahoo.com
///---------------------------------------------------------------------------

import React, {useState, useEffect, useRef} from 'react'
import { faSpinner, faChevronRight, faChevronLeft } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { arrayFromDateString, toFormatDate } from '../../services/DateService';
import { getXMLVat, vatUrlString } from '../../services/ValidationService';
import { downloadXmlFomString, getXMLGeneratedData, IntrastatXmlError } from '../../services/XMLService';
import { validateIntrastatXml } from '../../services/XmlValidationService';
import { createNotification } from '../../services/NotificationService';
import { currentLinesAvailable } from '../../services/SubscriptionsService';
import { exportExcel } from '../../services/ExcelService';
import { useAuth } from '../../context/AuthContext';
// import { bigID } from '../../services/IdService';
import ModalSetValue from './ModalSetValue';
import Popup from '../Popup';
import {
    VAT_CHECK_COUNTER_COOKIE,
    VAT_CHECK_COOKIE,
    setMinuteCookie,
    checkCookie,
    getCookie
} from '../../services/CookieService';
import {
    FIELD,
    FIELD_LABELS,
    ITEM,
    SEVERITY,
    asString,
    getRequiredSupplUnit,
    normalizeField,
    splitPartnerVat,
    toDecimalString,
    validateField,
} from '../../services/IntrastatRulesService';

const ExcelResultTable = ({data, actions}) => {
    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Limit of rows to be displayed per table page
    //-----------------------------------------------------------------------------------------
    const PER_PAGE = 50;

    //-----------------------------------------------------------------------------------------
    //* \brief
    //? How many findings are listed in the generation summary before it is truncated
    //-----------------------------------------------------------------------------------------
    const MAX_FINDINGS_SHOWN = 60;

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Name like in database of header strings that are required
    //-----------------------------------------------------------------------------------------
    const REQUIRED_HEADERS = [
        'codVamal',          // 00
        'valoareFiscala',    // 01
        'greutateNeta',      // 02
        'conditieLivrare',   // 03
        'taraOrigine',       // 04
        'taraExpediere',     // 05
        'valoareStatistica', // 06
        'naturaTranzactiei', // 07
        'modTransport',      // 08
        'codUms',            // 09
        'valoareUms',        // 10
        // 'vat',            // 11
    ];

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Empty array used to fill the Ref to create multidimensional array
    //-----------------------------------------------------------------------------------------
    const emptyFillerArray = new Array(data.headers.length + REQUIRED_HEADERS.length + 1).fill(null).map(() => [])

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Deconstruction of useAuth()
    //-----------------------------------------------------------------------------------------
    const {
        addInGeneratedHistory,
        newStatsGeneratedXml,
        getExchangeRates,
        updateUserLines,
        currentRates,
        currentUser,
        companies,
        userData,
        layouts,
    } = useAuth()

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? States for exchange action
    //-----------------------------------------------------------------------------------------
    const [checkedVats, setCheckedVats] = useState(new Set())
    const [errorVat, setErrorVat]       = useState(new Set())

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? States for loading to display for user
    //-----------------------------------------------------------------------------------------
    const [loadingAction, setLoadingAction] = useState(false)

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? States
    //-----------------------------------------------------------------------------------------
    const [hasErrors, setHasErrors]             = useState(false)
    const [repairLog, setRepairLog]             = useState([])
    const [errorCells, setErrorCells]           = useState([[]])
    const [errorIndexes, setErrorIndexes]       = useState(new Set())
    const [valStatArr, setValStatArr]           = useState([])
    const prevActionsRef                        = useRef({...actions, valStat: false, showErr: false})
    const [modal, setModal]                     = useState(false);
    const [popupOn, setPopupOn]                 = useState(false);
    const [popupData, setPopupData]             = useState(false);
    const [modalValues, setModalValues]         = useState({});
    const [loading, setLoading]                 = useState(true);
    const [page, setPage]                       = useState(0);
    const [reqHeadersIndex, setReqHeadersIndex] = useState(new Set());
    const [columnMap, setColumnMap]             = useState({});
    const [additionIndex, setAdditionIndex]     = useState({});
    const [headerStrings, setHeaderStrings]     = useState([]);
    const [footerStrings, setFooterStrings]     = useState([]);
    const [tableData, setTableData]             = useState({
        lines: 0,
        headers: data.headers,
        totalFisc: 0,
        totalStat: 0,
        totalNetW: 0,
        width: [],
        active: [],
        values: [],
        types: []
    });

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Refs used for changing values displayed for user in table
    //-----------------------------------------------------------------------------------------
    const SelectorRefs = useRef(
        emptyFillerArray.map(() => {
            let headerData = []
            data.sheetValues.forEach(val => {
                let toAdd = ''
                headerData.push(toAdd)
            })
            return headerData
        })
    )

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Populate the states with the required columns nr
    //-----------------------------------------------------------------------------------------
    const populateReqHeaders = async () => {
        let newReqHeaders = [...REQUIRED_HEADERS];
        layouts[data.layout].type === 'livrari' && newReqHeaders.push('vat') // 11
        let reqIndex = new Set()
        let colMap = {}
        let available = new Set()
        for (const [key, value] of Object.entries(layouts[data.layout].data)) {
            if(key.slice(-4) !== 'Impl') {
                if(value !== false && value !== 'false' && value !== '') {
                    if(newReqHeaders.includes(key.toString())) {
                        available.add(key.toString())
                        reqIndex.add(parseInt(value))
                        colMap[key.toString()] = parseInt(value)
                    }
                }
            }
        }
        const missing = newReqHeaders.filter(val => !available.has(val))
        setReqHeadersIndex(reqIndex)
        setColumnMap(colMap)
        await new Promise(r => setTimeout(r, 150));
        return {missing, colMap, reqIndex}
    }

    //-----------------------------------------------------------------------------------------
    //* \brief
    //? Returns layout field value if it is set and valid, otherwise null.
    //? Replaces repeated Object.entries loops for layout data access.
    //-----------------------------------------------------------------------------------------
    const getLayoutField = (fieldName) => {
        const val = layouts[data.layout].data[fieldName]
        if (val === false || val === 'false' || val === '') return false
        return val
    }

    //-----------------------------------------------------------------------------------------
    //* \brief
    //? Checks if header is required by index
    //-----------------------------------------------------------------------------------------
    const isHeaderRequired = (index) => {
        return reqHeadersIndex.has(index)
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Returns string with default value from layout
    //-----------------------------------------------------------------------------------------
    const getLayoutDefaultData = (index, keyName = '') => {
        const suffix = 'Impl'
        let dataToGet = ''
        if(keyName === '') {
            for (const [key, value] of Object.entries(layouts[data.layout].data)) {
                if(value.toString() === index.toString()) {
                    dataToGet = key.toString() + suffix
                    break
                }
            }
        }
        else dataToGet = `${keyName}${suffix}`

        const raw = layouts[data.layout].data[dataToGet]
        if (raw === undefined || raw === false || raw.toString() === 'false') return ''
        return raw.toString()
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Exchanges values to RON if needed
    //-----------------------------------------------------------------------------------------
    // @deprecated — replaced by checkExchangeNew
    // const checkExchange = async () => {
    //     if (loading) return
    //     // Collect all exchange updates, then apply in one batch at the end
    //     const exchUpdates = {} // rowIndex -> calculated value
    //     let exchIndex = -1
    //     try {
    //     await new Promise(r => setTimeout(r, 100));
    //     exchIndex = getLayoutField('valoareFiscala')
    //     if(exchIndex === false) exchIndex = columnMap[FIELD.VALOARE_FISCALA]
    //     const needsExchange = []
    //     let gotFromDb = true;

    //     if(!currentRates.dataId) gotFromDb = false

    //     // curs valutar
    //     const rateIndex = getLayoutField('cursDeSchimb')
    //     const rateIndexImpl = layouts[data.layout].data['cursDeSchimbImpl']

    //     // valoare valuta
    //     const currValueIndex = getLayoutField('valoareValuta')
    //     const currValueIndexImpl = layouts[data.layout].data['valoareValutaImpl']

    //     // data tranzactie
    //     const dateIndex = getLayoutField('dataTranzactiei')
    //     const dateIndexImpl = layouts[data.layout].data['dataTranzactieiImpl']

    //     // moneda
    //     const currIndex = getLayoutField('moneda')
    //     const currIndexImpl = layouts[data.layout].data['monedaImpl']

    //     tableData.values[exchIndex].forEach((value, index) => {
    //         // needs exchange
    //         if(value === 0 || value === '' || value === '0') {
    //             needsExchange.push(index)
    //         }
    //     })
    //     // no need for exchange
    //     if (needsExchange.length === 0) return
    //     // missing valoare valuta
    //     if (currValueIndex === false && currValueIndexImpl === '') {
    //         createNotification('error', `Nu se poate calcula valoarea fiscală din cauza lipsei câmpului "Valoare valută" din machetă`)
    //         return
    //     }

    //     let rest = new Array(...needsExchange)
    //     // exchange with rate given from user
    //     if (rateIndex !== false || rateIndexImpl !== '') {
    //         for (let i = 0; i < needsExchange.length; i++) {
    //             const rateNow = rateIndex === false ? rateIndexImpl : tableData.values[rateIndex][needsExchange[i]]
    //             if (rateNow !== '') {
    //                 const toMultiply = currValueIndex === false ? currValueIndexImpl : tableData.values[currValueIndex][needsExchange[i]]
    //                 if (toMultiply !== '') {
    //                     const retVal = (parseFloat(rateNow) * parseFloat(toMultiply)).toFixed(2)
    //                     exchUpdates[needsExchange[i]] = retVal
    //                     rest.splice(rest.indexOf(needsExchange[i]), 1)
    //                 }
    //             }
    //         }
    //     }
    //     // exchange with rates from database
    //     if (dateIndex === false && dateIndexImpl === '') {
    //         createNotification('error', `Nu se poate calcula valoarea fiscală din cauza lipsei datei tranzacției`)
    //         return
    //     }
    //     if (currIndex === false && currIndexImpl === '') {
    //         createNotification('error', `Nu se poate calcula valoarea fiscală din cauza lipsei monedei tranzacției`)
    //         return
    //     }

    //     let rates = {}
    //     // how many times the db is called
    //     let dbCallCounter = 0
    //     const DB_CALL_ALLOWED = 3
    //     if (dateIndex !== false || dateIndexImpl !== '') {
    //         if (currIndex !== false || currIndexImpl !== '') {
    //             for (let i = 0; i < rest.length; i++) {
    //                 const toMultiply = currValueIndex === false ? currValueIndexImpl : tableData.values[currValueIndex][rest[i]]
    //                 const dateNow = dateIndex === false ? dateIndexImpl : tableData.values[dateIndex][rest[i]]
    //                 if (dateNow !== '') {
    //                     const currNow = currIndex === false ? currIndexImpl : tableData.values[currIndex][rest[i]]
    //                     if (currNow !== '') {

    //                         // If it is RON/LEU, rate is x1
    //                         if(currNow.toString() === 'RON' || currNow.toString() === 'LEU') {
    //                             exchUpdates[rest[i]] = toMultiply
    //                             continue
    //                         }

    //                         const dateMY = arrayFromDateString(dateNow)
    //                         dateMY[0] = parseInt(dateMY[0]);
    //                         dateMY[1] = parseInt(dateMY[1]);
    //                         dateMY[2] = parseInt(dateMY[2]);
    //                         if (dateMY.length < 3) break;
    //                         if (!gotFromDb && dbCallCounter <= DB_CALL_ALLOWED) {
    //                             rates = await getExchangeRates(dateMY[1], dateMY[2])
    //                             gotFromDb = true
    //                             dbCallCounter++
    //                         }
    //                         else if (!rates.dataId) {
    //                             rates = currentRates
    //                         }

    //                         // first check dates
    //                         if(rates.data && rates.data['month-year'] !== `${dateMY[1]}-${dateMY[2]}` && dbCallCounter <= DB_CALL_ALLOWED) {
    //                             rates = await getExchangeRates(dateMY[1], dateMY[2])
    //                             dbCallCounter++
    //                         }
    //                         // dates not available?
    //                         if(rates.data && rates.data['month-year'] !== `${dateMY[1]}-${dateMY[2]}`) {
    //                             break;
    //                         }

    //                         // Find the rate for this date, falling back to previous days
    //                         // if the exact date is missing (weekends, holidays, etc.)
    //                         const dateToCheck = new Date(dateMY[2], dateMY[1] - 1, dateMY[0])
    //                         let rateEntry = null
    //                         const MAX_LOOKBACK = 7
    //                         for (let d = 0; d < MAX_LOOKBACK; d++) {
    //                             const checkDate = new Date(dateToCheck)
    //                             checkDate.setDate(checkDate.getDate() - d)
    //                             const dayKey = `${checkDate.getFullYear()}-${String(checkDate.getMonth() + 1).padStart(2, '0')}-${String(checkDate.getDate()).padStart(2, '0')}`
    //                             if (rates.data[dayKey] !== undefined) {
    //                                 rateEntry = rates.data[dayKey]
    //                                 break
    //                             }
    //                         }

    //                         // calculate with rates from db
    //                         if (rateEntry) {
    //                             for (const [key, val] of Object.entries(rateEntry)) {
    //                                 if (key.toString() === currNow.toString()) {
    //                                     const retVal = (parseFloat(val.rate) *
    //                                         parseFloat(toMultiply) *
    //                                         parseFloat(val.multiplyer)).toFixed(2)
    //                                     exchUpdates[rest[i]] = retVal
    //                                     break
    //                                 }
    //                             }
    //                         }
    //                     }
    //                 }
    //             }
    //         }
    //     }
    //     } catch (error) {
    //         // createNotification('error', 'Eroare la calculul cursului de schimb. Verificați datele introduse.')
    //     } finally {
    //         // Apply all collected exchange updates in a single atomic state update
    //         const updateKeys = Object.keys(exchUpdates)
    //         if (updateKeys.length > 0 && exchIndex !== -1) {
    //             // Update DOM refs for immediate visual feedback
    //             for (const rowIdx of updateKeys) {
    //                 const idx = parseInt(rowIdx)
    //                 if (SelectorRefs.current[exchIndex] && SelectorRefs.current[exchIndex][idx]) {
    //                     SelectorRefs.current[exchIndex][idx].value = exchUpdates[rowIdx]
    //                     SelectorRefs.current[exchIndex][idx].dispatchEvent(new Event('blur', { bubbles: true }))
    //                 }
    //             }
    //             // Single atomic state update
    //             setTableData(prev => {
    //                 const newCol = [...prev.values[exchIndex]]
    //                 for (const rowIdx of updateKeys) {
    //                     newCol[parseInt(rowIdx)] = exchUpdates[rowIdx]
    //                 }
    //                 const newValues = prev.values.map((col, idx) =>
    //                     idx === exchIndex ? newCol : col
    //                 )
    //                 return { ...prev, values: newValues }
    //             })
    //         }
    //         await new Promise(r => setTimeout(r, 250));
    //         setTotalValues();
    //     }
    // }

    //-----------------------------------------------------------------------------------------
    //* \brief
    //? Resolves a layout field: returns per-row value from the table column if the field
    //? points to a column index, or the implicit (fixed) value, or null if neither exists.
    //-----------------------------------------------------------------------------------------
    const resolveField = (fieldName, rowIndex) => {
        const colIdx = getLayoutField(fieldName)
        const implVal = layouts[data.layout].data[fieldName + 'Impl']
        if (colIdx !== false) {
            return tableData.values[colIdx]?.[rowIndex] ?? null
        }
        if (implVal !== undefined && implVal !== '' && implVal !== false && implVal !== 'false') {
            return implVal
        }
        return null
    }

    //-----------------------------------------------------------------------------------------
    //* \brief
    //? Finds the BNR exchange rate entry for a given date, walking backwards up to 7 days
    //? to handle weekends and holidays. Returns the day's currency map or null.
    //-----------------------------------------------------------------------------------------
    const findRateEntry = (ratesData, day, month, year) => {
        const baseDate = new Date(year, month - 1, day)
        for (let d = 0; d < 7; d++) {
            const check = new Date(baseDate)
            check.setDate(check.getDate() - d)
            const key = `${check.getFullYear()}-${String(check.getMonth() + 1).padStart(2, '0')}-${String(check.getDate()).padStart(2, '0')}`
            if (ratesData[key] !== undefined) return ratesData[key]
        }
        return null
    }

    //-----------------------------------------------------------------------------------------
    //* \brief
    //? Calculates VALOARE_FISCALA for all rows that need currency exchange.
    //? Converts foreign currency amounts to RON using either a user-provided rate
    //? (from a table column or layout implicit) or BNR exchange rates from the DB.
    //? Applies all updates in a single atomic setTableData call.
    //-----------------------------------------------------------------------------------------
    const checkExchangeNew = async () => {
        if (loading) return

        const exchColIdx = getLayoutField('valoareFiscala') !== false
            ? getLayoutField('valoareFiscala')
            : columnMap[FIELD.VALOARE_FISCALA]

        if (exchColIdx === undefined) return
        const exchColumn = tableData.values[exchColIdx]
        if (!exchColumn) return

        // 1. Find ALL rows that need exchange (empty or zero VALOARE_FISCALA)
        const rowsToProcess = []
        for (let i = 0; i < exchColumn.length; i++) {
            const v = exchColumn[i]
            if (v === '' || v === 0 || v === '0') {
                rowsToProcess.push(i)
            }
        }
        if (rowsToProcess.length === 0) return

        // 2. Check that we have 'valoareValuta' (required to calculate)
        const hasValutaCol = getLayoutField('valoareValuta') !== false
        const hasValutaImpl = (() => {
            const v = layouts[data.layout].data['valoareValutaImpl']
            return v !== undefined && v !== '' && v !== false && v !== 'false'
        })()
        if (!hasValutaCol && !hasValutaImpl) {
            createNotification('error', 'Nu se poate calcula valoarea fiscală din cauza lipsei câmpului "Valoare valută" din machetă')
            return
        }

        // 3. Process each row — collect results, never call setTableData in the loop
        const results = {} // rowIndex -> calculated string value
        // Cache: "month-year" -> rates object from DB
        const ratesCache = {}

        for (let i = 0; i < rowsToProcess.length; i++) {
            const row = rowsToProcess[i]

            const foreignAmount = resolveField('valoareValuta', row)
            if (foreignAmount === null || foreignAmount === '') continue

            // --- Path A: user-provided exchange rate ---
            const userRate = resolveField('cursDeSchimb', row)
            if (userRate !== null && userRate !== '') {
                const val = (parseFloat(userRate) * parseFloat(foreignAmount)).toFixed(2)
                if (!isNaN(parseFloat(val))) {
                    results[row] = val
                    continue
                }
            }

            // --- Path B: DB exchange rate ---
            const currency = resolveField('moneda', row)
            if (currency === null || currency === '') continue

            // RON/LEU → rate is x1
            const curr = currency.toString().toUpperCase()
            if (curr === 'RON' || curr === 'LEU') {
                results[row] = parseFloat(foreignAmount).toFixed(2)
                continue
            }

            const dateStr = resolveField('dataTranzactiei', row)
            if (dateStr === null || dateStr === '') continue

            const dateParts = arrayFromDateString(dateStr)
            if (dateParts.length < 3) continue

            const day = parseInt(dateParts[0])
            const month = parseInt(dateParts[1])
            const year = parseInt(dateParts[2])
            if (isNaN(day) || isNaN(month) || isNaN(year)) continue

            const monthYearKey = `${month}-${year}`

            // Fetch rates from DB (with caching per month-year)
            let rates = ratesCache[monthYearKey]
            if (!rates) {
                if (currentRates?.data?.['month-year'] === monthYearKey) {
                    rates = currentRates
                } else {
                    try {
                        rates = await getExchangeRates(month, year)
                    } catch {
                        continue
                    }
                }
                if (rates?.data) {
                    ratesCache[monthYearKey] = rates
                } else {
                    continue
                }
            }

            // Find the rate entry for this date (walks back up to 7 days)
            const dayRates = findRateEntry(rates.data, day, month, year)
            if (!dayRates) continue

            const currencyData = dayRates[curr] || dayRates[currency.toString()]
            if (!currencyData) continue

            const val = (
                parseFloat(currencyData.rate) *
                parseFloat(foreignAmount) *
                parseFloat(currencyData.multiplyer)
            ).toFixed(2)
            if (!isNaN(parseFloat(val))) {
                results[row] = val
            }
        }

        // 4. Apply all results in one atomic operation
        const updatedRows = Object.keys(results)
        if (updatedRows.length === 0) return

        // Update DOM refs for immediate visual feedback
        for (const rowIdx of updatedRows) {
            const idx = parseInt(rowIdx)
            const el = SelectorRefs.current[exchColIdx]?.[idx]
            if (el) el.value = results[rowIdx]
        }

        // Single atomic state update
        setTableData(prev => {
            const newCol = [...prev.values[exchColIdx]]
            for (const rowIdx of updatedRows) {
                newCol[parseInt(rowIdx)] = results[rowIdx]
            }
            const newValues = prev.values.map((col, idx) =>
                {
                    return +idx === +exchColIdx ? newCol : col
                }
            )
            return { ...prev, values: newValues }
        })

        // Recalculate totals
        await new Promise(r => setTimeout(r, 10000));
        setTotalValues()
    }

    //-----------------------------------------------------------------------------------------
    //* \brief
    //? Aligns the two UMS cells of ONE row with what the CN8 nomenclature requires for
    //? cn8Value.
    //?
    //? cn8Value is passed in rather than read from tableData on purpose. The previous
    //? version looked the code up in tableData right after updateCell() had queued its
    //? setTableData, so it still saw the code selected BEFORE the change: picking a code
    //? that needs a supplementary unit did nothing, and the next pick - even one needing
    //? no unit - filled in the unit of the previous code. It also swept every row of the
    //? table on each pick, which could rewrite rows the user never touched.
    //-----------------------------------------------------------------------------------------
    const applyUmsForRow = (rowIndex, cn8Value) => {
        const codUmsIdx = columnMap[FIELD.COD_UMS]
        const valUmsIdx = columnMap[FIELD.VALOARE_UMS]
        if (codUmsIdx === undefined && valUmsIdx === undefined) return

        const required = getRequiredSupplUnit(cn8Value)
        // unknown cod vamal - reported as an error on its own column
        if (required === null) return

        const cellValue = (colIdx) =>
            colIdx === undefined ? '' : asString(tableData.values[colIdx]?.[rowIndex]).trim()

        // this code has no supplementary unit -> both cells must be empty
        if (required === '') {
            if (codUmsIdx !== undefined && cellValue(codUmsIdx) !== '') {
                updateCell(codUmsIdx, rowIndex, '')
            }
            if (valUmsIdx !== undefined && cellValue(valUmsIdx) !== '') {
                updateCell(valUmsIdx, rowIndex, '')
            }
            return
        }

        // already the right unit - leave the quantity the user may have typed alone
        if (cellValue(codUmsIdx) === required) return

        if (codUmsIdx !== undefined) updateCell(codUmsIdx, rowIndex, required)
        if (valUmsIdx !== undefined) {
            const bucField = getLayoutField('cantitate')
            const bucIdx = bucField !== false ? parseInt(bucField) : -1
            updateCell(valUmsIdx, rowIndex, bucIdx === -1 ? '' : cellValue(bucIdx))
        }
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Returns cell value to display in table
    //-----------------------------------------------------------------------------------------
    const populateCellValue = (
        index,
        value,
        i,
        changeState = true,
        tValues = tableData.values,
        colMap = columnMap
    ) => {
        if(value !== '') {
            return value
        }
        else {
            // check if currency needs to be converted
            if(index === colMap[FIELD.VALOARE_FISCALA]) {
                //? kept as a string: a numeric 0 is falsy and used to slip through the
                //? row validation, which is how zero invoice values reached the XML
                const toReturn = '0'
                changeState && handleCellChange(index, i, toReturn)
                return toReturn
            }
            // check cod ums and valoare ums
            else if(index === colMap[FIELD.COD_UMS] || index === colMap[FIELD.VALOARE_UMS]) {
                const bucField = getLayoutField('cantitate')
                const bucIdx = bucField !== false ? parseInt(bucField) : -1
                // no bucati data in layout
                if(bucIdx === -1 && index === colMap[FIELD.VALOARE_UMS]) return value

                if(!returnVerify(value, index, i, tValues, colMap)) {
                    if(index === colMap[FIELD.VALOARE_UMS]) { // Val UMS
                        const retVal = tValues[bucIdx][i]
                        changeState && handleCellChange(index, i, retVal)
                        return retVal
                    } else if (index === colMap[FIELD.COD_UMS]) { // Cod UMS
                        const required = getRequiredSupplUnit(tValues[colMap[FIELD.COD_VAMAL]][i])
                        if(required !== null && required !== '') {
                            changeState && handleCellChange(index, i, required)
                            return required
                        }
                    }
                }
                return value
            }
            else {
                const toReturn = getLayoutDefaultData(index)
                changeState && handleCellChange(index, i, toReturn)
                return toReturn
            }
        }
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Returns the title of the header to be displayed
    //-----------------------------------------------------------------------------------------
    const getHeaderTitle = (index) => {
        if(!isHeaderRequired(index)) {
            return tableData.headers[index].toString()
        }
        let text = tableData.headers[index].toString()
        for (const [key, value] of Object.entries(layouts[data.layout].data)) {
            if(value.toString() === index.toString()) {
                text = key
                break
            }
        }
        const res = text.replace(/([A-Z])/g, " $1");
        const finalRes = res.charAt(0).toUpperCase() + res.slice(1)
        return finalRes.toString()
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Populates values for header and footer of columns
    //-----------------------------------------------------------------------------------------
    const populateHeaderFooterStrings = async () => {
        let arr = []
        let footerArr = []
        let addInd = {}
        for (let i = 0; i < tableData.headers.length; i++) {
            arr.push(getHeaderTitle(i))
            if(isHeaderRequired(i)) {
                for (const [key, value] of Object.entries(layouts[data.layout].data)) {
                    if(i.toString() === value.toString()) {
                        switch(key) {
                            case 'valoareFiscala': {
                                addInd.valoareFiscala = i
                                footerArr[i] = tableData.totalFisc
                                break;
                                }
                            case 'valoareStatistica': {
                                addInd.valoareStatistica = i
                                footerArr[i] = tableData.totalStat
                                break;
                                }
                            case 'greutateNeta': {
                                addInd.greutateNeta = i
                                footerArr[i] = tableData.totalNetW
                                break;
                                }
                            default:
                                footerArr.push('')
                                break;
                        }
                    }
                }
            }
            else footerArr.push('')
        }
        await new Promise(r => setTimeout(r, 150));
        setAdditionIndex(addInd)
        await new Promise(r => setTimeout(r, 150));
        setFooterStrings(footerArr)
        await new Promise(r => setTimeout(r, 150));
        JSON.stringify(arr) !== JSON.stringify(headerStrings) && setHeaderStrings(arr);
        await new Promise(r => setTimeout(r, 150));
        setTotalValues()
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Populates entire table
    //-----------------------------------------------------------------------------------------
    const populateTable = async() => {
        const response = await populateReqHeaders()
        const {missing, colMap, reqIndex} = response
        let width =  []
        let active = []
        let values = []
        let types =  []

        // const dataSheets = data.detailed ? data.sheetValues : addDuplicates()
        const dataSheets = data.sheetValues
        await new Promise(r => setTimeout(r, 150));
        let lines = dataSheets.length
        let headers = data.headers;

        const headerLoop = data.headers
        headerLoop.forEach(header => {
            let headerData = []
            dataSheets.forEach(val => {
                let toAdd = ''
                for (const [key, value] of Object.entries(val)) {
                    if(key === header) {
                        toAdd = value.toString()
                        break
                    }
                }
                headerData.push(toAdd)
            })
            values.push(headerData)
            active.push(true) //// TODO based on layout
            width.push(7) //// TODO based on layout
            types.push('text') //// TODO based on layout
        })
        // check if there are missing required columns and fill them with default data
        if(missing.length > 0) {
            missing.forEach((val) => {
                reqIndex.add(headers.length)
                colMap[val.toString()] = headers.length
                headers.push(val)
                let additional = []

                values[0].forEach(() => {
                    const textData = getLayoutDefaultData(-1, val.toString())
                    additional.push(textData)
                })
                values.push(additional)
                active.push(true) //// TODO based on layout
                width.push(7) //// TODO based on layout
                types.push('text') //// TODO based on layout
            })
            setReqHeadersIndex(reqIndex)
            setColumnMap(colMap)
        }

        // Checking for errors and adding missing values from layouts
        await new Promise(r => setTimeout(r, 150));
        // row
        for (let i = 0; i < values[0].length; i++) {
            // column
            for (let j = 0; j < values.length; j++) {
                const newVal = populateCellValue(j, values[j][i], i, false, values, colMap)
                // if not UMS
                if (j !== colMap[FIELD.COD_UMS] && j !== colMap[FIELD.VALOARE_UMS] && !hasErrors) {
                    // if is still empty -> error
                    if (newVal === '' && isHeaderRequired(j)) {
                        setHasErrors(true)
                    }
                }
                values[j][i] = newVal
            }
        }

        // repair everything that can be repaired deterministically before the user sees it
        const repairs = autoRepairValues(values, colMap)

        setTableData(prev => ({...prev, headers, lines, width, active, values}))

        await new Promise(r => setTimeout(r, 150));
        if (repairs.length > 0) {
            setRepairLog(repairs)
            createNotification('info',
                `${repairs.length} ${repairs.length === 1 ? 'valoare a fost corectată automat' : 'valori au fost corectate automat'}. ` +
                `Apăsați pe "Vezi corecții automate" (sub tabel) pentru detalii.`)
        }

        // display size warnings for user
        await new Promise(r => setTimeout(r, 150));
        if (values[0].length > 999 && values[0].length < 4998) {
            createNotification('warning', 'Acesta este un fișier mare. Unele funcții vor necesita mai mult timp.')
        }
        else if (values[0].length > 4999) {
            createNotification('warning', 'Acesta este un foarte fișier mare. Unele funcții vor necesita mult timp.')
        }
    }

    //-----------------------------------------------------------------------------------------
    //* \brief
    //? Repairs, in place, every mapped cell that can be repaired without guessing:
    //?   - "3208 90 99" / "3208.90.99" -> "32089099"        (NC8 separators)
    //?   - "7104000"                   -> "07104000"        (leading zero eaten by Excel)
    //?   - "3208909900"                -> "32089099"        (TARIC reduced to NC8)
    //?   - "at" / " AT "               -> "AT"              (country codes)
    //?   - "42"                        -> "4.2"             (nature of transaction)
    //?   - "12,4" / "12.4"             -> "12"              (whole kg / whole RON)
    //?   - "03"                        -> "3"               (mode of transport)
    //?   - the supplementary unit code is rewritten from the NC8 nomenclature
    //? Anything ambiguous is deliberately left untouched so it is reported as an error.
    //? Returns the list of corrections so the user always sees what was changed.
    //-----------------------------------------------------------------------------------------
    const autoRepairValues = (values, colMap) => {
        const corrections = []
        if (!values || !values[0]) return corrections

        const cn8Col = colMap[FIELD.COD_VAMAL]
        const rowCount = values[0].length

        for (let row = 0; row < rowCount; row++) {
            Object.keys(colMap).forEach(fieldName => {
                const col = colMap[fieldName]
                if (col === undefined || !values[col]) return

                const before = asString(values[col][row])
                const result = normalizeField(fieldName, before)
                if (!result.changed) return

                values[col][row] = result.value
                corrections.push({
                    row: row + 1,
                    label: FIELD_LABELS[fieldName] || fieldName,
                    message: result.note,
                })
            })

            // the supplementary unit is dictated by the NC8 nomenclature, never by the file
            if (cn8Col === undefined || !values[cn8Col]) continue
            const required = getRequiredSupplUnit(values[cn8Col][row])
            if (required === null) continue

            const umsCol = colMap[FIELD.COD_UMS]
            const qtyCol = colMap[FIELD.VALOARE_UMS]
            if (umsCol !== undefined && values[umsCol]) {
                const current = asString(values[umsCol][row]).trim()
                if (current !== required) {
                    values[umsCol][row] = required
                    corrections.push({
                        row: row + 1,
                        label: FIELD_LABELS[FIELD.COD_UMS],
                        message: required === ''
                            ? `Codul NC8 "${values[cn8Col][row]}" nu are unitate suplimentară - "${current}" a fost eliminat`
                            : `Unitatea suplimentară "${current}" înlocuită cu "${required}" conform codului NC8 "${values[cn8Col][row]}"`,
                    })
                }
            }
            // a quantity without a unit would produce an invalid <InsSupplUnitsInfo>
            if (required === '' && qtyCol !== undefined && values[qtyCol]) {
                const currentQty = asString(values[qtyCol][row]).trim()
                if (currentQty !== '') {
                    values[qtyCol][row] = ''
                    corrections.push({
                        row: row + 1,
                        label: FIELD_LABELS[FIELD.VALOARE_UMS],
                        message: `Cantitatea "${currentQty}" eliminată: codul NC8 nu are unitate suplimentară`,
                    })
                }
            }
        }
        return corrections
    }

    //-----------------------------------------------------------------------------------------
    //* \brief
    //? Walks every row and every mapped column and collects the official rule findings.
    //? Returns { errors, warnings, rows } where errors block generation and warnings only
    //? need the user's acknowledgement.
    //-----------------------------------------------------------------------------------------
    const buildValidationReport = (tValues = tableData.values, colMap = columnMap) => {
        const errors = []
        const warnings = []
        const rows = []

        if (!tValues || !tValues[0]) return { errors, warnings, rows }

        const fields = Object.keys(colMap)
        for (let i = 0; i < tValues[0].length; i++) {
            const rowCells = []
            const ruleCtx = buildRuleContext(i, tValues, colMap)

            for (let f = 0; f < fields.length; f++) {
                const fieldName = fields[f]
                const colIndex = colMap[fieldName]
                if (colIndex === undefined || !tValues[colIndex]) continue

                //? NOTE: the value is passed through untouched. The previous version
                //? skipped every falsy value that was not the empty string, so a numeric
                //? 0 invoice value or net mass was never validated at all.
                const finding = validateField(fieldName, tValues[colIndex][i], ruleCtx)
                if (!finding) continue

                const entry = {
                    row: i + 1,
                    field: fieldName,
                    label: FIELD_LABELS[fieldName] || fieldName,
                    ...finding,
                }
                rowCells.push(entry)
                if (finding.severity === SEVERITY.ERROR) errors.push(entry)
                else warnings.push(entry)
            }
            if (rowCells.length > 0) rows.push({ row: i + 1, cells: rowCells })
        }
        return { errors, warnings, rows }
    }
    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Function to parse to float, but return 0 if values is not a number
    //-----------------------------------------------------------------------------------------
    const parseAddFloat = (nr) => {
        if (nr === '' || parseFloat(nr).toString() === 'NaN') return 0
        return parseFloat(nr)
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Sum values for columns that require it
    //-----------------------------------------------------------------------------------------
    const sumColumn = (columnValues, fieldName) => {
        const defaultVal = getLayoutDefaultData(-1, fieldName)
        return columnValues.reduce(
            (prev, cur) => parseAddFloat(prev) + parseAddFloat(cur !== '' ? cur : defaultVal),
            0
        )
    }

    const setTotalValues = () => {
        const sums = {}
        tableData.values.forEach((val, index) => {
            for (const [key, value] of Object.entries(additionIndex)) {
                if(index.toString() === value.toString()) {
                    sums[key] = sumColumn(val, key)
                }
            }
        })

        const valoareFiscalaSum = sums.valoareFiscala || 0
        const valoareStatisticaSum = sums.valoareStatistica || 0
        const greutateNetaSum = sums.greutateNeta || 0

        if(valoareFiscalaSum === 0 && valoareStatisticaSum === 0 && greutateNetaSum === 0) return

        setTableData(prev => ({...prev,
            totalFisc: valoareFiscalaSum.toLocaleString(),
            totalStat: valoareStatisticaSum.toLocaleString(),
            totalNetW: greutateNetaSum.toLocaleString()}))
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Checks if column can be displayed
    //-----------------------------------------------------------------------------------------
    const checkToDisplayColumns = (index) => {
        if(data.onlyReq) {
            return isHeaderRequired(index)
        }
        return true
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Checks if there is error in row
    //-----------------------------------------------------------------------------------------
    const checkErrorsFilter = (rowIndex, quick = false) => {
        const errCells = []
        const fields = Object.keys(columnMap)
        for(let i = 0; i < fields.length; i++) {
            const fieldName = fields[i]
            const index = columnMap[fieldName]
            if (!tableData.values[index]) continue; // Skip undefined column
            //? Only a cell the file never produced is skipped. Anything else - including
            //? a numeric 0 - is validated, otherwise zero values silently reach the XML.
            if (tableData.values[index][rowIndex] === undefined) continue;
            if(!returnVerify(tableData.values[index][rowIndex], index, rowIndex)) {
                if (quick) return { val: false }
                errCells.push(fieldName)
            }
        }
        if(errCells.length > 0) return { val: false, cell: errCells }
        return { val: true, cell: [] }
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Checks if row can be displayed if it has errors
    //-----------------------------------------------------------------------------------------
    const checkToDisplayRows = (index) => {
        if(data.showErr) {
            return !checkErrorsFilter(index, true).val
        }
        return true
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Checks if row has errors
    //-----------------------------------------------------------------------------------------
    const isRowError = (index, wantCell = false) => {
        const toReturn = checkErrorsFilter(index, false)
        if (!wantCell) return { val: !toReturn.val, cells: [] }
        return { val: !checkErrorsFilter(index).val, cells: toReturn.cell }
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Changes the state that stores the table values
    //-----------------------------------------------------------------------------------------
    const handleCellChange = (bigIndex, smallIndex, val) => {
        setTableData(prev => {
            const newValues = prev.values.map((col, idx) =>
                idx === bigIndex
                    ? col.map((cell, i) => (i === smallIndex ? val : cell))
                    : col
            );
            return { ...prev, values: newValues };
        })
    }

    //-----------------------------------------------------------------------------------------
    //* \brief
    //? Updates both the DOM input (via ref) and the React state for a cell.
    //? Use this for all programmatic cell updates to keep ref and state in sync.
    //-----------------------------------------------------------------------------------------
    const updateCell = (colIndex, rowIndex, val) => {
        if (SelectorRefs.current[colIndex] && SelectorRefs.current[colIndex][rowIndex]) {
            SelectorRefs.current[colIndex][rowIndex].value = val
        }
        handleCellChange(colIndex, rowIndex, val)
    }

    //-----------------------------------------------------------------------------------------
    //* \brief
    //? Handles page change by number given
    //-----------------------------------------------------------------------------------------
    const handleInputPageChange = (e) => {
        const input = e - 1
        if(input < 0) return 0
        else if(input >= Math.ceil(tableData.lines/PER_PAGE)) return Math.ceil(tableData.lines/PER_PAGE)-1
        else return input
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Handle page change by button click
    //-----------------------------------------------------------------------------------------
    const pageChangeBtn = (input) => {
        const toSet = page
        if(input === -1 && page > 0) setPage(toSet - 1)
        if(input === 1 && page < Math.ceil(tableData.lines/PER_PAGE)-1) setPage(toSet + 1)
    }

    //-----------------------------------------------------------------------------------------
    //* \brief
    //? Builds the context a field rule needs from the rest of the row.
    //-----------------------------------------------------------------------------------------
    const buildRuleContext = (smallIndex, tValues, colMap) => {
        const cn8Column = tValues[colMap[FIELD.COD_VAMAL]]
        return {
            type: layouts[data.layout].type,
            isValStat: data.valStat,
            cn8: (smallIndex !== -1 && cn8Column) ? asString(cn8Column[smallIndex]).trim() : '',
            knownInvalidVats: errorVat,
        }
    }

    //-----------------------------------------------------------------------------------------
    //* \brief
    //? Returns the official rule finding for a cell, or null when the cell is valid.
    //? All rules live in IntrastatRulesService so the table, the XML generator and the
    //? final XML check can never disagree.
    //-----------------------------------------------------------------------------------------
    const verifyCell = (
        value,
        index,
        smallIndex = -1,
        tValues = tableData.values,
        colMap = columnMap
    ) => {
        const fieldName = Object.keys(colMap).find(name => colMap[name] === index)
        if (!fieldName) return null
        return validateField(fieldName, value, buildRuleContext(smallIndex, tValues, colMap))
    }

    //-----------------------------------------------------------------------------------------
    //* \brief
    //? Checks if value given is considered error. Returns false if there is error.
    //-----------------------------------------------------------------------------------------
    const returnVerify = (
        value,
        index,
        smallIndex = -1,
        tValues = tableData.values,
        colMap = columnMap
    ) => {
        const finding = verifyCell(value, index, smallIndex, tValues, colMap)
        return finding === null || finding.severity !== SEVERITY.ERROR
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Check if cell value is of type select (fake select inout)
    //-----------------------------------------------------------------------------------------
    const isSelectStyle = (index) => {
        // check non-required - currency
        if(layouts[data.layout].data.moneda === index) return true

        // check required select-style columns
        if( index === columnMap[FIELD.COD_VAMAL] ||
            index === columnMap[FIELD.CONDITIE_LIVRARE] ||
            index === columnMap[FIELD.TARA_ORIGINE] ||
            index === columnMap[FIELD.TARA_EXPEDIERE] ||
            index === columnMap[FIELD.NATURA_TRANZACTIEI] ||
            index === columnMap[FIELD.MOD_TRANSPORT]
        ) return true
        else return false
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Handles open modal box
    //-----------------------------------------------------------------------------------------
    const openModal = (bigIndex, smallIndex) => {
        let type = 0
        // set type if not required
        // currency
        if(layouts[data.layout].data.moneda === bigIndex) {
            type = -1
        }
        else {
            // Find the field name for this column index, then get its REQUIRED_HEADERS position
            const fieldName = Object.keys(columnMap).find(name => columnMap[name] === bigIndex)
            type = fieldName ? REQUIRED_HEADERS.indexOf(fieldName) : 0
        }

        setModal(true)
        const valuesForModal = {
            value: SelectorRefs.current[bigIndex][smallIndex].value,
            bigIndex,
            smallIndex,
            type
        }
        setModalValues(valuesForModal)
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Function passed to modal box child to pass values back to parent(this)
    //-----------------------------------------------------------------------------------------
    const setValueFromModal = (val, bigIndex, smallIndex) => {
        updateCell(bigIndex, smallIndex, val)
        //? `val` is handed over directly - tableData still holds the previous value at
        //? this point. Keyed off the column index instead of `type`, because openModal
        //? falls back to type 0 (cod vamal) for any column it cannot name.
        if (bigIndex === columnMap[FIELD.COD_VAMAL]) applyUmsForRow(smallIndex, val)
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Calls function to export excel from Excel service
    //-----------------------------------------------------------------------------------------
    const exportExcelAction = () => {
        exportExcel(tableData.values, tableData.headers)
    }

    const getErrorRows = (rows = tableData.values) => {
        const errorRows = []
        rows.forEach((value, index) => {
            const errorRow = []
            rows[index].forEach((v, i) => {
                isRowError(i).val && errorRow.push(v)
            })
            errorRow.length > 0 && errorRows.push(errorRow)
        })
        return errorRows
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Exports excel with errors only
    //-----------------------------------------------------------------------------------------
    const exportExcelErrorsAction = () => {
        const errorRows = getErrorRows()
        exportExcel(errorRows, tableData.headers, 'Erori exportate din programintrastat.ro')
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Checks VAT from table values using API
    //-----------------------------------------------------------------------------------------
    const checkVatAction = async () => {
        if(loadingAction) return

        // cookie logic
        if(checkCookie(VAT_CHECK_COOKIE)) {
            return createNotification('info', `Așteptați puțin pentru a putea verifica codurile VAT din nou`)
        }
        const counter = checkCookie(VAT_CHECK_COUNTER_COOKIE) ?
            (parseInt(getCookie(VAT_CHECK_COUNTER_COOKIE)) + 1) : 0

        let timeout = 1
        if(counter < 2) timeout = 0
        else if(counter > 1 && counter <  6) timeout = 1
        else if(counter > 5 && counter < 12) timeout = 2
        else timeout = 5

        setMinuteCookie(VAT_CHECK_COOKIE, true, timeout)
        setMinuteCookie(VAT_CHECK_COUNTER_COOKIE, counter, 10)
        // end cookie logic

        setLoadingAction(true)
        createNotification('info', 'Se verifică valorile VAT. Așteptați până se finalizează animația de încărcare.')
        let vatIndex = getLayoutField('vat')
        if(vatIndex === false) {
            vatIndex = parseInt(
                prompt(`Numărul coloanei cu valorile VAT lipsește din machetă. Introduceți numărul coloanei [1 - ${tableData.headers.length}]:`, 1))-1
            if(vatIndex === null || 
                vatIndex === '' || 
                vatIndex.toString() === 'NaN' || 
                vatIndex === undefined ||
                vatIndex > tableData.headers.length-1)
                    return createNotification('error', 'Valoare invalidă')
        }
        const allVats = tableData.values[vatIndex]
        const errorVats = errorVat
        const checked = checkedVats
        allVats.forEach(_vat => {
            // TODO: CHECK WHY validateVATEu is sometimes false
            // if(!validateVATEu(vat, true)) errorVats.add(vat)
        })
        const vatsToCheck = Array.from(new Set(allVats.filter(vat => !checked.has(vat) && !errorVats.has(vat))))

        // QV codes are placeholder codes, mark as valid without API check
        vatsToCheck.filter(vat => vat?.toUpperCase().startsWith('QV')).forEach(vat => checked.add(vat))
        const vatsToCheckFiltered = vatsToCheck.filter(vat => !checked.has(vat))

        // Start checking vat with API
        let isError = false
        for(let i = 0; i < vatsToCheckFiltered.length; i++) {
            const result = splitPartnerVat(vatsToCheckFiltered[i])
            //? no country prefix - the structural rule already reports it, do not guess one
            if(!result) {
                errorVats.add(vatsToCheckFiltered[i])
                continue
            }
            await new Promise(r => setTimeout(r, 300));
            try {
                const countryCode = result[0] === 'GR' ? 'EL' : result[0] // For Greece, check with correct country code (EL instead of GR)
                const response = await fetch(vatUrlString(countryCode, result[1]))
                const message = await response.json()
                if(!message.valid) errorVats.add(vatsToCheckFiltered[i])
            } catch {
                // Do nothing on API error
                // isError = true
                // errorVats.add(vatsToCheck[i])
            }
        }

        vatsToCheck.forEach(vat => checked.add(vat))
        setErrorVat(errorVats)
        setCheckedVats(checked)
        setLoadingAction(false)
        isError ? 
            createNotification('error', 'Au existat erori la verificarea valorilor VAT') : 
            createNotification('success', 'Valorile VAT au fost verificate cu succes!')
        
        errorVats.size > 0 && createNotification('error', `Există ${errorVats.size} valori VAT invalide.`)
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Sets a fake loading timer on first display of table
    //-----------------------------------------------------------------------------------------
    const loadingOnStart = async () => {
        const loadingTime = 3000;
        if(!loading) setLoading(true)
        await new Promise(r => setTimeout(r, loadingTime));
        setLoading(false)
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Calculates statistical value
    //-----------------------------------------------------------------------------------------
    const calcValStat = async () => {
        setLoadingAction(true)
        // semn +/- -> EXW, FCA = PLUS -> rest = MINUS
        const rows = []
        tableData.values[0].forEach((v, index) => {
            const row = []
            tableData.values.forEach((val) => {
                row.push(val[index])
            })
            rows.push(row)
        })
        const reqArray = []
        rows.forEach((row) => {
            let arr = []
            row.forEach((r, i) => {
                if(
                    i === columnMap[FIELD.CONDITIE_LIVRARE] ||
                    i === columnMap[FIELD.MOD_TRANSPORT] ||
                    i === columnMap[FIELD.NATURA_TRANZACTIEI] ||
                    i === columnMap[FIELD.TARA_EXPEDIERE]
                ) {
                    if(i === columnMap[FIELD.CONDITIE_LIVRARE])   arr[0] = r
                    else if(i === columnMap[FIELD.MOD_TRANSPORT])      arr[1] = r
                    else if(i === columnMap[FIELD.NATURA_TRANZACTIEI]) arr[2] = r
                    else if(i === columnMap[FIELD.TARA_EXPEDIERE])     arr[3] = r
                }
            })
            if(arr[0] === 'EXW' || arr[0] === 'FCA') arr.push('PLUS')
            else arr.push('MINUS')
            arr.push(0)
            reqArray.push(arr)
        })
        // [0] cond liv, [1] mod trans, [2] nat tranz, [3] tara,
        const finalArr = Array.from(new Set(reqArray.map(JSON.stringify)), JSON.parse).sort()
        setValStatArr(finalArr)
        await new Promise(r => setTimeout(r, 400));
        setLoadingAction(false)
    }

    //-----------------------------------------------------------------------------------------
    //* \brief
    //? Sets resulted values in table view
    //-----------------------------------------------------------------------------------------
    const setValStatVal = async (res) => {
        setValStatArr(res)
        // map val stat
        tableData.values[columnMap[FIELD.VALOARE_STATISTICA]].forEach((value, index) => {
            res.forEach(r => {
                // can't calculate with 0
                if(r[5] === 0) return

                if(
                    r[0] === tableData.values[columnMap[FIELD.CONDITIE_LIVRARE]][index] &&
                    r[1] === tableData.values[columnMap[FIELD.MOD_TRANSPORT]][index] &&
                    r[2] === tableData.values[columnMap[FIELD.NATURA_TRANZACTIEI]][index] &&
                    r[3] === tableData.values[columnMap[FIELD.TARA_EXPEDIERE]][index]
                ) {
                    const currVal = parseFloat(tableData.values[columnMap[FIELD.VALOARE_FISCALA]][index])
                    if(currVal === 0 || currVal === '') return
                    const percent = parseFloat(currVal * parseFloat(parseFloat(r[5])/100))
                    const retVal = r[4] === 'PLUS' ? parseFloat(currVal + percent).toFixed(2) :
                    parseFloat(currVal - percent).toFixed(2)
                    handleCellChange(columnMap[FIELD.VALOARE_STATISTICA], index, retVal.toString())
                    return
                }
            })
        })
        await new Promise(r => setTimeout(r, 400));
        setTotalValues()
    }

    //-----------------------------------------------------------------------------------------
    //* \brief
    //? Open Popup with statistical value user interface
    //-----------------------------------------------------------------------------------------
    const openValStat = () => {
        if(valStatArr.length < 1) {
            createNotification('error', 'Datele nu au fost extrase încă din fișier. Încercați din nou')
            return
        }
        const dataForPopup = {
            valStatArr: valStatArr,
            title: 'Valoare statistică',
            type: 1,
            func: setValStatVal
        }
        setPopupData(dataForPopup)
        setPopupOn(true)
    }

    // 'codVamal',          // 00 *
    // 'valoareFiscala',    // 01
    // 'greutateNeta',      // 02
    // 'conditieLivrare',   // 03 *
    // 'taraOrigine',       // 04 *
    // 'taraExpediere',     // 05 *
    // 'valoareStatistica', // 06
    // 'naturaTranzactiei', // 07 *
    // 'modTransport',      // 08 *
    // 'codUms',            // 09
    // 'valoareUms',        // 10
    //! 'vat',              // 11 **
    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Generates an array only with the required values for final export
    //-----------------------------------------------------------------------------------------
    const getArrayWithRequired = (arr = tableData.values, colMap = columnMap) => {
        // Build ordered field list matching original REQUIRED_HEADERS order
        const fieldOrder = [...REQUIRED_HEADERS]
        if (layouts[data.layout].type === 'livrari') fieldOrder.push(FIELD.VAT)
        const reqArray = []
        arr[0].forEach((val, rowIndex) => {
            //? Always a dense array: a hole at the end used to shift the partner VAT,
            //? because the generator read it as item[item.length - 1]
            let rowArr = new Array(fieldOrder.length).fill('')
            fieldOrder.forEach((fieldName, fieldIdx) => {
                const colIdx = colMap[fieldName]
                if (colIdx !== undefined && arr[colIdx]) {
                    rowArr[fieldIdx] = asString(arr[colIdx][rowIndex])
                }
            })
            reqArray.push(rowArr)
        })
        return reqArray
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Sums duplicates from array based on required fields
    //-----------------------------------------------------------------------------------------
    const addArrayDuplicates = (arr) => {
        const finalArr = []
        const duplicates = new Set()
        arr.forEach((value, index) => {
            if(duplicates.has(index)) return

            let currentRow = value
            let totalValFisc = parseAddFloat(value[ 1])
            let totalNetW    = parseAddFloat(value[ 2])
            let totalValStat = parseAddFloat(value[ 6])
            let totalBuc     = parseAddFloat(value[10])
            for(let i = index + 1; i < arr.length; i++) {
                if(
                    value[0] === arr[i][0] &&
                    value[3] === arr[i][3] &&
                    value[4] === arr[i][4] &&
                    value[5] === arr[i][5] &&
                    value[7] === arr[i][7] &&
                    value[8] === arr[i][8] 
                ) {
                    if(layouts[data.layout].type === 'livrari') {
                        if(value[ITEM.PARTNER_VAT] === arr[i][ITEM.PARTNER_VAT]) {
                            totalValFisc += parseAddFloat(arr[i][ 1])
                            totalNetW    += parseAddFloat(arr[i][ 2])
                            totalValStat += parseAddFloat(arr[i][ 6])
                            totalBuc     += parseAddFloat(arr[i][10])
                            duplicates.add(i)
                        }
                    }
                    else {
                        totalValFisc += parseAddFloat(arr[i][ 1])
                        totalNetW    += parseAddFloat(arr[i][ 2])
                        totalValStat += parseAddFloat(arr[i][ 6])
                        totalBuc     += parseAddFloat(arr[i][10])
                        duplicates.add(i)
                    }
                }
            }
            //? The exact sums are kept here, decimals included. The XML generator rounds
            //? them once, afterwards - rounding each line before adding would inflate the
            //? declaration (2.5 + 5.5 + 1.2 is 9, but 3 + 6 + 1 is 10).
            currentRow[ITEM.INVOICE_VALUE]  = toDecimalString(totalValFisc)
            currentRow[ITEM.NET_MASS]       = toDecimalString(totalNetW)
            currentRow[ITEM.STAT_VALUE]     = toDecimalString(totalValStat)
            currentRow[ITEM.SUPPL_UNIT_QTY] = toDecimalString(totalBuc)
            finalArr.push(currentRow)
        })
        // Sort all items based on criteria
        finalArr.sort((a, b) => 
            // Vamal Code            #1
            a[0].localeCompare(b[0]) || 
            // C of Origin           #2
            a[4].localeCompare(b[4]) ||
            // C of Exped            #3
            a[5].localeCompare(b[5]) ||
            // Terms of exped        #4
            a[3].localeCompare(b[3])
        )
        return finalArr
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Creates the XML file for the user to download
    //-----------------------------------------------------------------------------------------
    const generateXML = async (type, values, generatedArr) => {
        setPopupOn(false)
        // inchide fereastra
        if(!type) return

        let comp = type !== 2 ? { cif : values.cName } : {}
        if(data.tert) { comp.name = companies[data.compFor].name }
        else { comp.name = companies[0].name }

        const month = parseInt(data.month) < 10 ? `0${data.month}` : `${data.month}`
        const typeLetter = layouts[data.layout].type.toUpperCase().charAt(0)
        let fileName = `${data.month}_${data.year}_${typeLetter}_${getXMLVat(userData.data.cui)}`
        if (data.tert) fileName += `_${getXMLVat(comp.cif)}`

        if(data.reviz) fileName += `_R`

        // nu mai genera
        if(type === 2) {
            const oldData = data.oldData[0]
            downloadXmlFomString(oldData, fileName)
            return
        }
        setLoadingAction(true)

        const itemArray = !data.isOldData ? generatedArr : 
        data.detailed ? getArrayWithRequired() : addArrayDuplicates(getArrayWithRequired())

        const splitName = values.reprez ? values.reprez.trim().split(/\s+/) : ''
        const fName = splitName ? splitName.map((curr, index) => {
            return (index < splitName.length - 1) ? curr : ''
        }).join(' ').trim() : ''
        const lName = splitName ? splitName[splitName.length - 1] : ''

        const toPassToXML = {
            isValStat: data.valStat,
            type:      layouts[data.layout].type,
            tert:      data.tert,
            reviz:     data.reviz,
            compVat:   getXMLVat(comp.cif),
            compName:  comp.name,
            refPeriod: `${data.year}-${month}`,
            email:     values.mail,
            phone:     values.phone,
            position:  values.pos,
            tertVat:   getXMLVat(userData.data.cui),
            tertName:  userData.data.company,
            county:    userData.data.county,
            itemArray,
            fName,
            lName
        }

        //? Two gates before anything is written to disk:
        //?  1. getXMLGeneratedData throws IntrastatXmlError if the data cannot produce a
        //?     declaration the official application accepts
        //?  2. validateIntrastatXml reads the finished document back and checks it against
        //?     the INS schema, exactly like the offline application does on import
        let xmlRes
        try {
            xmlRes = getXMLGeneratedData(toPassToXML)
        }
        catch (error) {
            setLoadingAction(false)
            if (error instanceof IntrastatXmlError) {
                createNotification('error', 'Generarea a fost oprită: datele nu respectă schema INTRASTAT.')
                openFindingsPopup('Generare oprită', error.findings)
                return
            }
            createNotification('error', 'Eroare la generarea fișierului XML.')
            return
        }

        const xmlCheck = validateIntrastatXml(xmlRes)
        if (!xmlCheck.valid) {
            setLoadingAction(false)
            createNotification('error', 'Fișierul generat nu a trecut verificarea față de schema INTRASTAT și nu a fost descărcat.')
            openFindingsPopup('Fișier respins de verificarea finală', xmlCheck.findings.map(f => ({
                row: f.row,
                label: f.label,
                message: f.message,
                severity: SEVERITY.ERROR,
            })))
            return
        }

        downloadXmlFomString(xmlRes, fileName)
        data.setOldData([xmlRes])
        if(!data.isOldData) {
            await updateUserLines(values.linesLeft)
        }
        // send statistics to database
        const today = new Date()
        const stat_data = {
            uid:         currentUser.uid,
            lines:       tableData.lines,
            linesUsedFromSub: tableData.lines,
            generatedFileName: fileName,
            fileName:    data.file.name,
            type:        layouts[data.layout].type,
            reviz:       data.reviz,
            company:     comp.cif,
            tertCompany: comp.cif === userData.data.cui ? false : userData.data.cui,
            layoutId:    layouts[data.layout].dataId,
            hasErrors:   hasErrors,
            valStat:     data.valStat,
            name:        values.reprez,
            position:    values.pos,
            date:        toFormatDate(today),
            timeStamp:   today,
            generatedAgain: data.isOldData,
            generatedXML: xmlRes
        }
        
        // TODO Activate statistics in build (done)
        setLoadingAction(false)
        try {
            await newStatsGeneratedXml(stat_data)
            addInGeneratedHistory(stat_data);
            createNotification('success', 'Declarația generată a fost salvată în secțiunea "Istoric declarații". O puteți descărca și de acolo.')
        }
        catch (error) {
            createNotification('warning', 'Declarația a fost generată, dar nu a putut fi salvată în istoric.')
        }
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Converts data to final array for XML export - Also checks if user has lines left in sub
    //-----------------------------------------------------------------------------------------
    const exportToXML = async () => {
        createNotification('info', 'Se generează. Vă rugăm așteptați..')
        setLoadingAction(true)
        try {
        await new Promise(r => setTimeout(r, 300));
        // Check for errors before generating
        const report = buildValidationReport()
        setHasErrors(report.errors.length > 0)
        if (report.errors.length > 0) {
            createNotification('error',
                `Generarea este blocată: ${report.errors.length} ${report.errors.length === 1 ? 'eroare' : 'erori'} în tabel.`)
        }
        else if (report.warnings.length > 0) {
            createNotification('warning', `Există ${report.warnings.length} avertismente în tabel.`)
        }

        let arrToDisplay = []
        if(!data.isOldData) {
            arrToDisplay = data.detailed ? getArrayWithRequired() : addArrayDuplicates(getArrayWithRequired())
        }
        const sub = userData.subscriptions[0]
        // const linesToGenerate = data.isOldData ? tableData.values[0].length : arrToDisplay.length
        const linesToGenerate = tableData.lines
        if(tableData.lines < 1) {
            createNotification('warning', 'Nu există linii pentru generat')
            setLoadingAction(false)
            return
        }
        // multiple exports/generates do not affect lines
        const linesAvailable = currentLinesAvailable(sub)
        //? An unreadable line count must stop generation. It used to fall through:
        //? sub.lines[idx] came back undefined, parseInt gave NaN, and `NaN < 0` is false,
        //? so the quota check silently passed and NaN was written back to the account.
        if(!data.isOldData && isNaN(linesAvailable)) {
            createNotification('error', 'Nu s-a putut determina numărul de linii disponibile în abonament. Reîncărcați pagina sau verificați secțiunea "Abonamente".')
            setLoadingAction(false)
            return
        }
        const linesLeft = data.isOldData ? 1 : linesAvailable - parseInt(linesToGenerate)
        if(isNaN(linesLeft) || linesLeft < 0) {
            createNotification('error', 'Nu mai aveți linii pentru generat disponibile în această lună pentru abonamentul actual')
            setLoadingAction(false)
            return
        }
        // check if 'livrari' and vat was checked
        //? gated on cifCheck, the flag that actually decides whether "Verificare VAT" can be
        //? pressed - it used to read valStatCalc, so a plan that grants one but not the other
        //? either nagged about a button the user cannot use or stayed silent when they can
        if (userData.subscriptions[0].cifCheck && layouts[data.layout].type === 'livrari' && checkedVats.size === 0) {
            createNotification('warning', `Valorile VAT nu au fost verificate înainte de generare. Apăsați 'Verificare VAT' înainte de a genera fișierul XML pentru a valida valorile VAT din tabel`)
        }
        let comp = {}
        if(data.tert) {
            comp.cif = companies[data.compFor].cif
        }
        else {
            comp.cif = userData.data.cui
        }
        const dataForPopup = {
            title:        'Rezumat generare',
            type:         2,
            func:         generateXML,
            tert:         data.tert,
            hasValStat:   data.valStat,
            uData:        userData.data,
            generatedArr: arrToDisplay,
            hasErr:       report.errors.length > 0,
            hasWarn:      report.warnings.length > 0,
            findings:     [...report.errors, ...report.warnings].slice(0, MAX_FINDINGS_SHOWN),
            findingsTotal: report.errors.length + report.warnings.length,
            errorCount:   report.errors.length,
            warningCount: report.warnings.length,
            exists:       data.isOldData,
            linesToGenerate,
            linesLeft,
            comp,
        }
        setPopupData(dataForPopup)
        setPopupOn(true)
        } catch (error) {
            createNotification('error', 'Eroare la pregătirea generării XML. Verificați datele.')
        }
        setLoadingAction(false)
    }

    //-----------------------------------------------------------------------------------------
    //* \brief
    //? Populates error cells 
    //-----------------------------------------------------------------------------------------
    const populateErrorCells = async () => {
        setLoadingAction(true)
        await new Promise(r => setTimeout(r, 200));
        const errorRows = new Set([])
        let errorsArr = 
            new Array(tableData.values.length).fill(null).map(() => new Array(tableData.values[0].length).fill(''))
        tableData.values[0].forEach((val, i) => {
            if (checkToDisplayRows(i)) {
                tableData.values.forEach((value, idx) => {
                    errorsArr[idx][i] = value[i]
                })
                errorRows.add(i)
            }
        })
        setErrorCells(errorsArr)
        setErrorIndexes(errorRows)
        setLoadingAction(false)
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Calls functions only on startup 
    //-----------------------------------------------------------------------------------------
    useEffect(() => {
        populateTable()
        loadingOnStart()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Checks if parent buttons are pressed and starts required actions
    //-----------------------------------------------------------------------------------------
    useEffect(() => {
        if (loading || loadingAction) return
        const prev = prevActionsRef.current

        if (prev.checkVat !== actions.checkVat) {
            checkVatAction()
        }
        if (prev.exportExcel !== actions.exportExcel) {
            exportExcelAction()
        }
        if (prev.exportExcelErr !== actions.exportExcelErr) {
            exportExcelErrorsAction()
        }
        if (prev.valStat !== data.valStat) {
            data.valStat && calcValStat()
        }
        if (prev.calcValStat !== actions.calcValStat) {
            data.valStat && openValStat()
        }
        if (prev.generate !== actions.generate) {
            exportToXML()
        }
        if (prev.downloadOldXML !== actions.downloadOldXML) {
            generateXML(2)
        }
        if (prev.showErr !== data.showErr) {
            data.showErr && createNotification('info', 'Se filtrează erorile. Vă rugăm așteptați..')
            data.showErr && populateErrorCells()
        }

        prevActionsRef.current = { ...actions, valStat: data.valStat, showErr: data.showErr }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actions, data.valStat, data.showErr, loading, loadingAction])

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Calls functions that update header and footer data of table
    //-----------------------------------------------------------------------------------------
    useEffect(() => {
        populateHeaderFooterStrings()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ reqHeadersIndex, headerStrings ])

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Calls sum of values function when indexes for addtitons are found
    //-----------------------------------------------------------------------------------------
    useEffect(() => {
        setTotalValues()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ additionIndex ])

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Checks if it is needed to exchange currency after first load
    //-----------------------------------------------------------------------------------------
    useEffect(() => {
        checkExchangeNew()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ loading ])

    //-----------------------------------------------------------------------------------------
    //* \brief
    //? Opens a Popup listing findings as "Linia N - Câmp: mesaj". Used for the per row
    //? error list, for the blocked generation report and for the auto-correction log.
    //-----------------------------------------------------------------------------------------
    const openFindingsPopup = (title, findings, footNote = '') => {
        const content = (
            <div className='popup-error-wrapper'>
                {findings.map((finding, index) => (
                    <div
                        className={`popup-error-row${finding.severity === SEVERITY.WARNING ? ' popup-error-row--warning' : ''}`}
                        key={`finding-${index}-${finding.row}-${finding.label}`}
                    >
                        {finding.row !== undefined && <b>Linia {finding.row}</b>}
                        {finding.row !== undefined && ' - '}
                        <b>{finding.label}</b>: {finding.message}
                    </div>
                ))}
                {footNote !== '' && <div className='popup-error-row'><i>{footNote}</i></div>}
            </div>
        )
        setPopupData({ title: `${title} (${findings.length})`, type: 3, content })
        setPopupOn(true)
    }

    //-----------------------------------------------------------------------------------------
    //* \brief
    //? Opens Popup with the official rule messages for one table row
    //-----------------------------------------------------------------------------------------
    const openShowErrors = (rowIndex, rowNr) => {
        const findings = Object.keys(columnMap).reduce((acc, fieldName) => {
            const col = columnMap[fieldName]
            if (col === undefined || !tableData.values[col]) return acc
            const finding = verifyCell(tableData.values[col][rowIndex], col, rowIndex)
            if (finding) acc.push({ label: FIELD_LABELS[fieldName] || fieldName, ...finding })
            return acc
        }, [])
        openFindingsPopup(`Erori pe linia ${rowNr}`, findings)
    }

    //-----------------------------------------------------------------------------------------
    //* \brief
    //? Checks if clicked button has error and IF SO opens error Popup
    //-----------------------------------------------------------------------------------------
    const handleCounterClick = (err, errArray) => {
        if (!err) return
        if (errArray.arr.length === 0) return
        openShowErrors(errArray.rowIndex, errArray.rowNr)
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? Return error rows
    //-----------------------------------------------------------------------------------------
    const returnAllErrors = () => {
        if (errorCells.length > 0) return errorCells
        else return [[]]
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? main function that returns JSX element of rows and cells
    //-----------------------------------------------------------------------------------------
    const mapReturnRows = () => {
        let counter = 0
        let errorsPerRow = []
        const toDisplay = data.showErr ? returnAllErrors() : tableData.values
        return toDisplay[0].map((value, i) => {
            // if error filter is on and the row has error
            if (data.showErr && !errorIndexes.has(i)) return null
            // increase line counter if row is valid
            counter++
            // if row is on page
            if(Math.ceil((counter+1)/(PER_PAGE)) === (page+1)) {
                const isRowErr = isRowError(i, true)
                if (isRowErr.val) { 
                    errorsPerRow[i] = { arr: isRowErr.cells, rowNr: counter, rowIndex: i }
                }
                // don't display row if error filter is on and there is no error on it
                if (data.showErr && !isRowErr.val) return null

                return(
                    <div className='res-table-row rows' key={`${value} - ${i}`}>
                        <input
                            id={`idx-${i}`}
                            type='text'
                            onClick={() => handleCounterClick(isRowErr.val, errorsPerRow[i])}
                            readOnly={true}
                            defaultValue={`${counter}`}
                            className={`res-table-input${isRowErr.val ? '  error-cell nr-crt' : ''}`}
                            style={{width: data.fullWidth ? 'min-content' : `3rem`, textAlign:'right'}}
                        />
                        {
                        toDisplay && toDisplay.map((val, index) => {
                            const isTypeSelect = isSelectStyle(index)
                            const cellValue = val ? val[i] : '' // populateCellValue(index, val[i], i)
                            const verify = returnVerify(cellValue, index, i)
                            if(checkToDisplayColumns(index) && index !== columnMap[FIELD.VALOARE_STATISTICA])
                            return(
                                <input
                                id={`data-input-${i}-${index}`}
                                defaultValue   = { cellValue }
                                disabled       = { !tableData.active[index] } 
                                readOnly       = { !tableData.active[index] } 
                                onClick        = { () => isTypeSelect && openModal(index, i)}
                                onBlur         = { (e) => handleCellChange(index, i, e.target.value) }
                                type           = { tableData.types[index] }
                                ref            = { (element) => SelectorRefs.current[index][i] = element }
                                key            = { `${index} - ${i}` }
                                className      = {
                                    `res-table-input${isTypeSelect ? 
                                    ' select-type' : 
                                    ''}${verify ?
                                    '' : ' error-cell'}`
                                }
                                style          = {{ 
                                    width: data.fullWidth ? 'min-content' : 
                                    `${tableData.width[index]}rem`
                                }}
                                />
                            )
                            // val statistica
                            else if(checkToDisplayColumns(index) && index === columnMap[FIELD.VALOARE_STATISTICA])
                            return(
                                <input
                                id={`data-input-vs-${i}-${index}`}
                                disabled    = {true} 
                                readOnly    = {true}
                                onBlur      = {(e) => handleCellChange(index, i, e.target.value)}
                                value       = {val[i] === '' ? cellValue : val[i]}
                                type        = {tableData.types[index]} 
                                ref         = {(element) => SelectorRefs.current[index][i] = element}
                                key         = {`${index} - ${i}`}
                                className   = {
                                    `res-table-input${verify ? '' : ' error-cell'}`
                                }
                                style       = {{
                                    width: data.fullWidth ? 'min-content' :
                                    `${tableData.width[index]}rem`
                                }}
                                />
                            )
                            else return null
                        })
                        }
                    </div>
                )
            }
            else {
                return null
            }
        })
    }

    //-----------------------------------------------------------------------------------------
    //* \brief 
    //? JSX Element returned
    //-----------------------------------------------------------------------------------------
    return (
        <>
        {loadingAction && 
        <div className='progress-bar-wrapper' style={{width:'100%', maxWidth:'100%'}}>
            <FontAwesomeIcon className='spinner' icon={faSpinner}/>
        </div>
        }
        <div className='res-table-wrapper'>
            <div className='res-table-section'>
                <div className='res-table-row header-cell'>
                    {/* TABLE HEADERS */}
                    <input 
                        id='Number00'
                        type="text" 
                        disabled     = {true} 
                        readOnly     = {true}
                        defaultValue = 'Nr.' 
                        className    = 'res-table-input'
                        style        = {{width: data.fullWidth ? 'min-content' : `3rem`}}
                    />
                    {
                    tableData.headers.map((header, index) => {
                        if(checkToDisplayColumns(index)) return(
                        <input 
                        id={`${header}${index}`}
                        defaultValue = {headerStrings[index]} 
                        className    = {`res-table-input ${isHeaderRequired(index) ? 'req': ''}`}
                        disabled     = {true} 
                        readOnly     = {true}
                        type         = "text" 
                        key          = {`${header} - ${index}`}
                        style        = {{
                            width:
                            data.fullWidth ? 'min-content' :
                            `${tableData.width[index]}rem`
                        }}
                        />
                        )
                        else return null
                    })
                    }
                </div>
                {/* TABLE CELLS */}
                {loading ? <FontAwesomeIcon className='spinner' icon={faSpinner}/> :
                tableData.values.length > 0 && mapReturnRows()
                }

                <div className='res-table-row footer-cell'> 
                    {/* TABLE FOOTERS */}
                    <input 
                        defaultValue = ''
                        className    = {`res-table-input`}
                        readOnly     = {true}
                        disabled     = {true} 
                        style        = {{width: data.fullWidth ? 'min-content' : `3rem`}}
                        type         = "text" 
                    />
                    {
                    tableData.headers && tableData.headers.map((header, index) => {
                        if(checkToDisplayColumns(index)) return(
                        <input 
                        defaultValue = {footerStrings[index]} 
                        className    = {`res-table-input ${isHeaderRequired(index) ? 'req': ''}`}
                        readOnly     = {true}
                        disabled     = {true} 
                        type         = "text"
                        key          = {`${[index]} - ${header}`}
                        style        = {{
                            width: data.fullWidth ? 'min-content' :
                            `${tableData.width[index]}rem`
                        }}
                        />
                        )
                        else return null
                    })

                    }
                </div>

            </div>
        </div>
        {Math.ceil(tableData.lines/PER_PAGE) > 1 && <><div className='page-btn-wrapper'>
            <div onClick={() => pageChangeBtn(-1)}>
                <FontAwesomeIcon icon={faChevronLeft}/>
            </div>

            <input type='number' id='page-input' value={page + 1} step='0'
            onChange={(e) => setPage(handleInputPageChange(e.target.value))} required/>

            <div onClick={() => pageChangeBtn(1)}> 
                <FontAwesomeIcon icon={faChevronRight}/>
            </div>

        </div>
        <div>Pagina <b>{page+1}</b> din {Math.ceil(tableData.lines/PER_PAGE)}</div></>}

        <div
            onClick={() => repairLog.length > 0 && openFindingsPopup(
                'Corecții automate',
                repairLog.slice(0, MAX_FINDINGS_SHOWN),
                repairLog.length > MAX_FINDINGS_SHOWN
                    ? `... și încă ${repairLog.length - MAX_FINDINGS_SHOWN} corecții.` : ''
            )}
            style={{cursor: repairLog.length > 0 ? 'pointer' : 'default'}}
            title={repairLog.length > 0 ? 'Vezi corecțiile automate' : ''}
        >
            Linii: {tableData.lines}
            {repairLog.length > 0 && <> &middot; <b>Vezi corecții automate ({repairLog.length})</b></>}
        </div>

        { modal && <ModalSetValue values = {modalValues} 
        close={() => setModal(false)} 
        setValue = {setValueFromModal} /> 
        }
        { popupOn && 
            <Popup data={popupData} close={() => setPopupOn(false)}> 
                {popupData.content}
            </Popup>
        }
        </>
    )
}

export default ExcelResultTable